import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CustomerResolutionService } from '../services/customer-resolution.service';
import { IdpService } from '../services/idp.api.service';
import { EmployeePortalConfigService } from '../../shared/services/customer-config.service';
import { BrandingStore } from '../../shared/stores/branding.store';
import { CustomerStore } from '../../shared/stores/customer.store';
import { AuthService, RETURN_URL_KEY } from '../services/auth.service';
import { AppNavigationService } from '../services/app-navigation.service';
import { PreviewModeService } from '../services/preview-mode.service';
import { EnvironmentService } from '../services/environment.service';
import { MagicLinkService } from '../services/magic-link.service';
import { SessionHandoffService } from '../services/session-handoff.service';
import { SSO_STORAGE_KEYS } from '../constants/sso.constants';
import { debugLog, debugWarn } from '../utils/debug-logger';

/**
 * Returns `true` when the error is an HTTP 404 Not Found response.
 * Used to distinguish "customer not found" (404) from other API failures.
 */
function isNotFoundError(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 404;
}

function getTargetPath(url: string): string {
  return url.split('?')[0].split('#')[0];
}

function isPublicPath(targetPath: string): boolean {
  const publicPaths = ['/admin-dashboard/employee-portal-configurations', '/admin-dashboard/customers'];
  return publicPaths.some((p) => targetPath === p || targetPath.startsWith(`${p}/`));
}

function createCustomerPathRedirect(router: Router, stateUrl: string, canonicalId: string) {
  const currentPath = getTargetPath(stateUrl);
  const customerPrefix = `/employee-view/customers/${canonicalId}`;
  const hasCustomerInPath = currentPath.startsWith(`${customerPrefix}/`);
  const isSpecialRoute = currentPath === '/' ||
    currentPath === customerPrefix ||
    currentPath.startsWith('/access-id') ||
    currentPath.startsWith('/error') ||
    currentPath.startsWith('/not-found') ||
    currentPath.startsWith('/auth/');

  if (hasCustomerInPath || isSpecialRoute) {
    return null;
  }

  const normalizedCurrent = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const redirectPath = `${customerPrefix}${normalizedCurrent}`;

  const urlParts = stateUrl.split('?');
  const fragment = urlParts[0].split('#')[1];
  const queryString = urlParts[1]?.split('#')[0];

  const navigationExtras: { queryParams?: Record<string, string>; fragment?: string } = {};
  if (queryString) {
    navigationExtras.queryParams = Object.fromEntries(new URLSearchParams(queryString));
  }
  if (fragment) {
    navigationExtras.fragment = fragment;
  }

  return router.createUrlTree([redirectPath], navigationExtras);
}

function createNotFoundRedirect(router: Router, source: string, resolvedId: string) {
  if (source === 'subdomain') {
    return router.createUrlTree(['/access-id'], {
      queryParams: { error: 'subdomain-not-found' },
    });
  }

  if (source === 'path' || source === 'override') {
    return router.createUrlTree(['/access-id'], {
      queryParams: { error: 'customer-not-found', attempted: resolvedId },
    });
  }

  localStorage.removeItem('customerId');
  return router.createUrlTree(['/access-id']);
}

/**
 * Root bootstrap guard that runs before any protected route is activated.
 *
 * Responsibilities:
 *  1. Resolve the active customer ID from the URL / localStorage.
 *  2. Validate the customer against the API to confirm it exists and the portal is enabled.
 *  3. Populate branding state (logo URL / tenant name) for the login screen.
 *  4. Store the customer ID in sessionStorage for use by the login request.
 *  5. Route to the appropriate error page if the customer is invalid or the API is down.
 *
 * Error routing table:
 *  - No customer context at all          → /access-id (silent — user must enter ID)
 *  - 404, source = subdomain             → /access-id?error=subdomain-not-found
 *  - 404, source = path or override      → /access-id?error=customer-not-found&attempted={}
 *  - 404, source = storage (stale entry) → /access-id (clears localStorage, silent)
 *  - portalEnabled = false               → /access-id?error=portal-disabled
 *  - API unavailable / unexpected error  → /error?code=api-unavailable
 *
 * Direct-entry routes (data.directEntry = true):
 *  On success with IDP    → full-browser redirect to Keycloak (return false)
 *  On success without IDP → /login
 */
export const customerBootstrapGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => { // NOSONAR
  const customerService = inject(CustomerResolutionService);
  const idpService = inject(IdpService);
  const configService = inject(EmployeePortalConfigService);
  const brandingStore = inject(BrandingStore);
  const customerStore = inject(CustomerStore);
  const authService = inject(AuthService);
  const router = inject(Router);
  const appNav = inject(AppNavigationService);
  const previewModeService = inject(PreviewModeService);
  const environmentService = inject(EnvironmentService);
  const magicLinkService = inject(MagicLinkService);
  const sessionHandoff = inject(SessionHandoffService);

  let magicLinkCustomerId: string | null = null;
  const urlParams = new URLSearchParams(state.url.split('?')[1] ?? '');
  const ctxParam = urlParams.get('ctx');
  if (ctxParam) {
    try {
      const context = await magicLinkService.decryptContext(ctxParam);
      if (magicLinkService.isValidContext(context)) {
        magicLinkCustomerId = context.customerId;
        sessionStorage.setItem('magic_link_target', JSON.stringify({
          disclosureId: context.disclosureId,
          campaignId: context.campaignId,
          formId: context.formId,
          formVersionId: context.formVersionId,
        }));
        customerStore.setCustomerId(context.customerId);
        localStorage.setItem('customerId', context.customerId);
        debugLog('BootstrapGuard', 'magic link detected', {
          customerId: context.customerId,
          disclosureId: context.disclosureId,
          campaignId: context.campaignId,
        });
      } else {
        debugWarn('BootstrapGuard', 'magic link context failed validation');
      }
    } catch (error) {
      debugWarn('BootstrapGuard', 'magic link decryption failed', error);
    }
  }

  // ── DEV-ONLY offline bypass ───────────────────────────────────────────────
  // When devAuthBypass is on (local environment.json only) skip customer
  // validation, the IDP lookup and the Keycloak redirect — they all require the
  // VPN-bound EP API / MyCM and fail offline. Prime the CustomerStore with the
  // configured dev customer so routing + the login flow work, then render the
  // matched child route (or route direct-entry users to home/login).
  if (environmentService.devAuthBypass) {
    const devCustomerId = environmentService.devCustomerId || 'ACME1';
    if (!customerStore.customerId()) {
      customerStore.setCustomerId(devCustomerId);
      customerStore.setSource('override');
      localStorage.setItem('customerId', devCustomerId);
    }
    if (route.data?.['directEntry']) {
      return authService.isAuthenticated ? appNav.createUrlTree('/home') : appNav.loginUrlTree();
    }
    return true;
  }

  // ── 1. Resolve customer identity ──────────────────────────────────────────
  // Always use the resolution service which implements the correct priority chain:
  // dev override → subdomain → localStorage → path segment
  // Route parameters (e.g., :customerId from app.routes.ts) are Angular routing artifacts
  // and must not override the resolution logic.
  // Use the target route URL (state.url) instead of current browser location to avoid
  // race conditions during navigation (e.g., resolving "access-id" when navigating to "/DEMO/login").
  const targetPath = getTargetPath(state.url);

  debugLog('BootstrapGuard', 'navigating to', {
    url: state.url,
    href: globalThis.location.href,
    hostname: globalThis.location.hostname,
    targetPath,
    localStorageCustomerId: localStorage.getItem('customerId'),
  });

  // Public routes that never need customer bootstrapping — skip immediately so that
  // a stale customerId in localStorage cannot trigger a validateCustomer API call.
  if (isPublicPath(targetPath)) {
    return true;
  }

  // Preview mode: previewGuard already bootstrapped customer and portal config —
  // skip the customer validation + IDP check entirely (AC#96759-6/12).
  if (previewModeService.isPreview() && customerStore.customerId()) {
    return true;
  }

  // Preview URL detection: the /portal-preview child route has its own previewGuard
  // that validates the preview payload (localStorage or URL hash), primes the
  // customer store, and enables preview mode.  Skip the full bootstrap (customer
  // validation, branding, IDP/SSO) so the guard does not interfere with the
  // preview flow — especially Keycloak SSO redirect which loses the hash fragment.
  if (targetPath.endsWith('/portal-preview')) {
    debugLog('BootstrapGuard', 'PREVIEW BYPASS — skipping bootstrap for', { targetPath });
    return true;
  }
  debugLog('BootstrapGuard', 'targetPath does NOT match portal-preview', { targetPath });

  const resolution = magicLinkCustomerId
    ? { customerId: magicLinkCustomerId, source: 'storage' as const }
    : customerService.resolve(
      globalThis.location.hostname,
      targetPath
    );

  debugLog('BootstrapGuard', 'resolution result', { resolution });

  if (!resolution) {
    // No customer context — send to the access-id entry page so the user can identify themselves.
    debugWarn('BootstrapGuard', 'no customer resolved — redirecting to /access-id');
    return router.createUrlTree(['/access-id']);
  }

  const { customerId: resolvedId, source } = resolution;
  const isDirectEntry = !!route.data?.['directEntry'];
  debugLog('BootstrapGuard', 'resolved customer', { resolvedId, source, isDirectEntry });

  try {
    // ── 2. Validate the customer ──────────────────────────────────────────────
    // Subdomain lookups use a separate endpoint because the subdomain is a
    // different field in the database (not the customer's access ID).
    const config = source === 'subdomain'
      ? await firstValueFrom(configService.validateCustomerBySubdomain(resolvedId))
      : await firstValueFrom(configService.validateCustomer(resolvedId));

    if (config.portalDeactivated) {
      return router.createUrlTree(['/access-id'], {
        queryParams: { error: 'portal-deactivated' },
      });
    }

    if (!config.portalEnabled) {
      return router.createUrlTree(['/access-id'], {
        queryParams: { error: 'portal-disabled' },
      });
    }

    // Use config.customerId (canonical ID from the API) for all downstream calls.
    // For subdomain entry the subdomain string may differ from the customer's access ID.
    const canonicalId = config.customerId;
    brandingStore.setLogoText(null);
    brandingStore.setLogoUrl(config.logoUrl);
    brandingStore.setIsLoginFormDisabled(config.isLoginFormDisabled ?? false);
    customerStore.setCustomerId(canonicalId);
    customerStore.setSource(source);

    // Always persist the canonical ID from the API response.
    // For subdomain entry the raw subdomain may differ from the canonical access ID (e.g. 'demoly' → 'DEMO').
    //For path/override entry, CustomerResolutionService.resolve() was bypassed, so this also covers that case.
    localStorage.setItem('customerId', canonicalId);

    // ── Enforce path-based routing (only for non-subdomain access) ─────────────
    // When customer is resolved from subdomain, routes are flat (no customer ID in path).
    // When customer is resolved from path/localStorage/override, ensure customer ID is in the path for clarity.
    if (source !== 'subdomain') {
      const redirect = createCustomerPathRedirect(router, state.url, canonicalId);
      if (redirect) {
        return redirect;
      }
    }


    // ── 3. IDP check — redirect to Keycloak if SSO is configured ─────────────
    const idp = await idpService.getIdp(canonicalId);

    // A magic link opened in a NEW tab starts with an empty `sessionStorage`, so
    // `isAuthenticated` is false here even when the employee is signed in in another tab.
    // This guard sits on the PARENT route and the SSO branch below returns `false` (a
    // full-browser redirect to Keycloak), so it would preempt `authGuard`'s cross-tab
    // hand-off entirely and make the employee sign in again for no reason.
    //
    // So ask the siblings first, but ONLY when a magic-link target is pending: every other
    // SSO entry point keeps its current shape, and an employee with no sibling session
    // still gets the automatic Keycloak redirect below.
    const adoptedFromSiblingTab = async (tenantId: string): Promise<boolean> => {
      if (!sessionStorage.getItem('magic_link_target')) {
        return false;
      }
      try {
        const offer = await sessionHandoff.requestSession(tenantId);
        return !!offer && authService.adoptSession(offer);
      } catch {
        // Best-effort only — fall through to the existing SSO redirect.
        return false;
      }
    };

    if (
      idp?.alias &&
      !authService.isAuthenticated &&
      !(await adoptedFromSiblingTab(canonicalId))
    ) {
      // SSO configured and user not yet authenticated — redirect to Keycloak with IDP hint.
      // Save the intended URL so the SSO callback can restore navigation after login.
      // directEntry routes (bare /ACME) have no meaningful intended destination yet.
      if (!isDirectEntry && !targetPath.endsWith('/login')) {
        // Angular's state.url does NOT include the URL hash fragment.
        // For preview URLs the hash carries the entire preview payload;
        // preserve it so the previewGuard can decode it after SSO return.
        let returnUrl = state.url;
        if (targetPath.endsWith('/portal-preview') && globalThis.location.hash) {
          returnUrl += globalThis.location.hash;
        }
        // Bug 149604 — the SHARED return-url key, not a per-flow `sso_return_url`.
        // This branch is only one of two ways a user reaches Keycloak (the other
        // is the login page's SSO buttons, which never wrote a key at all), and
        // the SSO callback reads exactly one key. See RETURN_URL_KEY.
        sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
      }
      sessionStorage.setItem(SSO_STORAGE_KEYS.PROVIDER, idp.alias);
      idpService.navigateToIdp(idpService.buildKeycloakLoginUrl(idp.alias, canonicalId));
      return false;
    }

    // ── 4. Determine next route ───────────────────────────────────────────────
    const magicLinkTarget = sessionStorage.getItem('magic_link_target');
    // Route to the form target even when `isAuthenticated` is false. This guard sits on the
    // PARENT route, so it resolves before `authGuard` and before the cross-tab session hand-off
    // can adopt a sibling session — a campaign link opened in a new tab always looks
    // unauthenticated here. Gating on `isAuthenticated` sent those users to the direct-entry
    // branch below (home when a session happened to exist, login otherwise) and discarded the
    // form the link named. `authGuard` on the child route makes the real authentication decision
    // and stores `/form/:id?campaignId=` as the return URL, so the form survives a sign-in.
    //
    // Exception: a link that lands on the login page itself. That page already owns the pending
    // target — LoginFormComponent and the SSO callback both read `magic_link_target` after a
    // successful sign-in — so redirecting to /form here would only bounce back through authGuard.
    // Fall through instead and leave the target in sessionStorage for them to consume.
    const isLoginTarget = targetPath.endsWith('/login');
    if (magicLinkTarget && (authService.isAuthenticated || !isLoginTarget)) {
      sessionStorage.removeItem('magic_link_target');
      try {
        const target = JSON.parse(magicLinkTarget) as { disclosureId: string; campaignId: number };
        return appNav.createUrlTree(`/form/${target.disclosureId}`, {
          queryParams: { campaignId: String(target.campaignId) },
        });
      } catch {
        // Fall through to the default post-authentication routing.
      }
    }

    if (isDirectEntry) {
      // Direct-entry routes (/ and /:customerId) have no child component to render;
      // authenticated users go straight to home, others go to the login form.
      return authService.isAuthenticated
        ? appNav.createUrlTree('/home')
        : appNav.loginUrlTree();
    }

    // Authenticated users must never see the login form. Browser session-restore
    // ("continue where you left off") re-populates sessionStorage with a still-valid
    // JWT after a close/reopen, so isAuthenticated is true even though the user expects
    // a fresh entry. Without this, the SSO branch above is skipped (idp.alias present but
    // isAuthenticated true) and the guard falls through to render the password login form
    // for an SSO customer. Send them to home instead.
    if (authService.isAuthenticated && targetPath.endsWith('/login')) {
      return appNav.createUrlTree('/home');
    }

    // Bootstrap-guarded group — fall through to the matched child route (e.g. /login).
    return true;
  } catch (err: unknown) {
    if (isNotFoundError(err)) {
      return createNotFoundRedirect(router, source, resolvedId);
    }

    // Unexpected API error (network failure, 5xx, etc.) — show the generic error page.
    return router.createUrlTree(['/error'], { queryParams: { code: 'api-unavailable' } });
  }
};
